import React, {CSSProperties} from "react";
import Overlay from "../Overlay";
import {sweetalert} from "../../App";
import {FacilityAPI} from "../../network/FacilityAPI";
import {ImportPreview} from "../../types/FacilityImport";
import {hideModal} from "../../util/FormatUtil";
import Dropzone from "react-dropzone";

const GREY = '#CCC';
const GREY_DIM = '#686868';

const dropzoneStyles = {
    zone: {
        alignItems: 'center',
        border: `2px dashed ${GREY}`,
        borderRadius: 20,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        justifyContent: 'center',
        padding: 20,
        marginBottom: 10,
        cursor: 'pointer',
        transition: 'border-color 0.2s ease'
    } as CSSProperties,
    zoneHover: {
        borderColor: GREY_DIM,
    } as CSSProperties,
    file: {
        background: 'linear-gradient(to bottom, #EEE, #DDD)',
        borderRadius: 20,
        display: 'flex',
        height: 120,
        width: 120,
        position: 'relative',
        zIndex: 10,
        flexDirection: 'column',
        justifyContent: 'center',
    } as CSSProperties,
    info: {
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'column',
        paddingLeft: 10,
        paddingRight: 10,
    } as CSSProperties,
    size: {
        backgroundColor: 'rgba(255, 255, 255, 0.4)',
        borderRadius: 3,
        marginBottom: '0.5em',
        justifyContent: 'center',
        display: 'flex',
    } as CSSProperties,
    name: {
        backgroundColor: 'rgba(255, 255, 255, 0.4)',
        borderRadius: 3,
        fontSize: 12,
        marginBottom: '0.5em',
        textAlign: 'center',
        wordBreak: 'break-all'
    } as CSSProperties,
    remove: {
        height: 23,
        position: 'absolute',
        right: 6,
        top: 6,
        width: 23,
        cursor: 'pointer',
        background: 'none',
        border: 'none',
        color: '#A01919',
        fontWeight: 'bold',
        fontSize: 16,
        lineHeight: '23px',
        textAlign: 'center',
        borderRadius: '50%'
    } as CSSProperties,
};

function formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

interface FacilityImportProps {
    onSuccess: () => void
}

interface FacilityImportState {
    showLoading: boolean
    screen: 'upload' | 'preview' | 'comparing' | 'processing'
    file: File | null
    facilityType: 'School' | 'Childcare' | null
    action: 'ImportNew' | 'Deactivate' | null
    preview: ImportPreview | null
    sessionId: string | null
    showDetails: boolean
    zoneHover: boolean
    progressProcessed: number
    progressTotal: number
}

export class FacilityImportModal extends React.Component<FacilityImportProps, FacilityImportState> {
    public static readonly ID = "facilityImport";

    constructor(props) {
        super(props);
        this.state = {
            showLoading: false,
            screen: 'upload',
            file: null,
            facilityType: null,
            action: null,
            preview: null,
            sessionId: null,
            showDetails: false,
            zoneHover: false,
            progressProcessed: 0,
            progressTotal: 0
        };
        this.handlePreview = this.handlePreview.bind(this);
        this.handleConfirm = this.handleConfirm.bind(this);
        this.resetState = this.resetState.bind(this);
    }

    private pollInterval: any = null;

    componentWillUnmount() {
        if (this.pollInterval) clearInterval(this.pollInterval);
    }

    resetState() {
        if (this.pollInterval) clearInterval(this.pollInterval);
        this.setState({
            showLoading: false,
            screen: 'upload',
            file: null,
            facilityType: null,
            action: null,
            preview: null,
            sessionId: null,
            showDetails: false,
            zoneHover: false,
            progressProcessed: 0,
            progressTotal: 0
        });
    }

    async handlePreview() {
        let {file, facilityType, action} = this.state;

        if (!file) {
            sweetalert.fire({icon: 'error', title: '', text: 'Please select a file.'});
            return;
        }
        if (!facilityType) {
            sweetalert.fire({icon: 'error', title: '', text: 'Please select a facility type.'});
            return;
        }
        if (!action) {
            sweetalert.fire({icon: 'error', title: '', text: 'Please select an action.'});
            return;
        }

        this.setState({screen: 'comparing', progressProcessed: 0, progressTotal: 0});

        try {
            let response = await FacilityAPI.facilityImportPreview(file, facilityType, action);
            if (!response.success) {
                sweetalert.fire({icon: 'error', title: '', text: response.reason});
                this.setState({screen: 'upload'});
                return;
            }

            let sessionId = response.sessionId;
            this.setState({sessionId});

            // Start polling for preview progress
            this.pollInterval = setInterval(async () => {
                try {
                    let progressResponse = await FacilityAPI.facilityImportProgress(sessionId, false);
                    if (!progressResponse.success) return;

                    let progress = progressResponse.progress;
                    this.setState({
                        progressProcessed: progress.processed,
                        progressTotal: progress.total
                    });

                    if (progress.status === 'complete') {
                        clearInterval(this.pollInterval);
                        this.pollInterval = null;

                        this.setState({
                            screen: 'preview',
                            preview: progress.result,
                            progressProcessed: 0,
                            progressTotal: 0
                        });
                    } else if (progress.status === 'error') {
                        clearInterval(this.pollInterval);
                        this.pollInterval = null;
                        sweetalert.fire({icon: 'error', title: '', text: progress.error || 'An error occurred while processing the file.'});
                        this.setState({screen: 'upload'});
                    }
                } catch (e) {
                    console.error('Preview progress poll error:', e);
                }
            }, 1000);

        } catch (e) {
            console.error(e);
            sweetalert.fire({icon: 'error', title: '', text: 'An error occurred while processing the file.'});
            this.setState({screen: 'upload'});
        }
    }

    async handleConfirm() {
        let {preview, sessionId} = this.state;
        if (!preview || !sessionId) return;

        let totalItems = preview.action === 'ImportNew' ? preview.toAdd.length : preview.toDeactivate.length;
        this.setState({screen: 'processing', progressProcessed: 0, progressTotal: totalItems});

        try {
            let response = await FacilityAPI.facilityImportConfirm(sessionId);
            if (!response.success) {
                sweetalert.fire({icon: 'error', title: '', text: response.reason});
                this.setState({screen: 'preview'});
                return;
            }

            // Start polling for progress (with cleanup since this is the final step)
            this.pollInterval = setInterval(async () => {
                try {
                    let progressResponse = await FacilityAPI.facilityImportProgress(sessionId, true);
                    if (!progressResponse.success) return;

                    let progress = progressResponse.progress;
                    this.setState({
                        progressProcessed: progress.processed,
                        progressTotal: progress.total
                    });

                    if (progress.status === 'complete') {
                        clearInterval(this.pollInterval);
                        this.pollInterval = null;

                        let result = progress.result;
                        let message = '';
                        if (preview.action === 'ImportNew') {
                            message = `Successfully added ${result.inserted} ${preview.facilityType.toLowerCase()}(s).`;
                            if (result.failed && result.failed.length > 0) {
                                message += ` ${result.failed.length} failed.`;
                            }
                        } else {
                            message = `Successfully deactivated ${result.deactivated} ${preview.facilityType.toLowerCase()}(s).`;
                            if (result.failed && result.failed.length > 0) {
                                message += ` ${result.failed.length} failed.`;
                            }
                        }

                        sweetalert.fire({icon: 'success', title: '', text: message});
                        this.resetState();
                        hideModal(FacilityImportModal.ID);
                        this.props.onSuccess();
                    } else if (progress.status === 'error') {
                        clearInterval(this.pollInterval);
                        this.pollInterval = null;
                        sweetalert.fire({icon: 'error', title: '', text: progress.error || 'An error occurred during import.'});
                        this.resetState();
                    }
                } catch (e) {
                    console.error('Progress poll error:', e);
                }
            }, 1000);

        } catch (e) {
            console.error(e);
            sweetalert.fire({icon: 'error', title: '', text: 'An error occurred during import.'});
            this.setState({screen: 'preview'});
        }
    }

    renderUploadScreen() {
        let {file, zoneHover} = this.state;

        return (
            <div>
                <div className="d-flex justify-content-between align-items-center mb-2">
                    <label className="verlag-bold mb-0">File Upload</label>
                    <span style={{color: '#A01919', fontSize: '0.85em', fontWeight: 'bold'}}>*file must be .csv or .xlsx</span>
                </div>

                <Dropzone
                    onDrop={(acceptedFiles, rejectedFiles) => {
                        this.setState({zoneHover: false});
                        if (rejectedFiles && rejectedFiles.length > 0) {
                            sweetalert.fire({icon: 'error', title: '', text: 'File must be .csv or .xlsx format.'});
                            return;
                        }
                        if (acceptedFiles && acceptedFiles.length > 0) {
                            this.setState({file: acceptedFiles[0]});
                        }
                    }}
                    onDragEnter={() => this.setState({zoneHover: true})}
                    onDragLeave={() => this.setState({zoneHover: false})}
                    accept={['.csv', '.xlsx', '.xls']}
                    multiple={false}
                >
                    {({getRootProps, getInputProps}) => (
                        <div
                            {...getRootProps()}
                            style={Object.assign(
                                {},
                                dropzoneStyles.zone,
                                zoneHover && dropzoneStyles.zoneHover
                            )}
                        >
                            <input {...getInputProps()} />
                            {file ? (
                                <div style={{display: 'flex', alignItems: 'center', gap: 15}}>
                                    <div style={dropzoneStyles.file}>
                                        <div style={dropzoneStyles.info}>
                                            <span style={dropzoneStyles.size}>{formatFileSize(file.size)}</span>
                                            <span style={dropzoneStyles.name}>{file.name}</span>
                                        </div>
                                        <button
                                            style={dropzoneStyles.remove}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                this.setState({file: null});
                                            }}
                                            title="Remove file"
                                        >
                                            &times;
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <span style={{color: '#686868'}}>Drop file here or click to upload</span>
                            )}
                        </div>
                    )}
                </Dropzone>

                <div className="form-group mb-3">
                    <label className="verlag-bold">Facility Type</label>
                    <div>
                        <div className="form-check form-check-inline">
                            <input
                                className="form-check-input"
                                type="radio"
                                name="facilityType"
                                id="typeSchool"
                                value="School"
                                checked={this.state.facilityType === 'School'}
                                onChange={() => this.setState({facilityType: 'School'})}
                            />
                            <label className="form-check-label" htmlFor="typeSchool">School</label>
                        </div>
                        <div className="form-check form-check-inline">
                            <input
                                className="form-check-input"
                                type="radio"
                                name="facilityType"
                                id="typeChildcare"
                                value="Childcare"
                                checked={this.state.facilityType === 'Childcare'}
                                onChange={() => this.setState({facilityType: 'Childcare'})}
                            />
                            <label className="form-check-label" htmlFor="typeChildcare">Childcare</label>
                        </div>
                    </div>
                </div>

                <div className="form-group mb-3">
                    <label className="verlag-bold">Action</label>
                    <div>
                        <div className="form-check form-check-inline">
                            <input
                                className="form-check-input"
                                type="radio"
                                name="importAction"
                                id="actionImport"
                                value="ImportNew"
                                checked={this.state.action === 'ImportNew'}
                                onChange={() => this.setState({action: 'ImportNew'})}
                            />
                            <label className="form-check-label" htmlFor="actionImport">Import New</label>
                        </div>
                        <div className="form-check form-check-inline">
                            <input
                                className="form-check-input"
                                type="radio"
                                name="importAction"
                                id="actionDeactivate"
                                value="Deactivate"
                                checked={this.state.action === 'Deactivate'}
                                onChange={() => this.setState({action: 'Deactivate'})}
                            />
                            <label className="form-check-label" htmlFor="actionDeactivate">Deactivate</label>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    renderPreviewScreen() {
        let {preview} = this.state;
        if (!preview) return null;

        let isImport = preview.action === 'ImportNew';
        let count = isImport ? preview.toAdd.length : preview.toDeactivate.length;
        let items = isImport ? preview.toAdd : preview.toDeactivate;

        return (
            <div>
                <div className="alert alert-info">
                    <strong>
                        {isImport
                            ? `${count} new ${preview.facilityType.toLowerCase()}(s) will be added.`
                            : `${count} ${preview.facilityType.toLowerCase()}(s) will be deactivated.`
                        }
                    </strong>
                    {isImport && preview.alreadyExist > 0 && (
                        <p className="mb-0 mt-1">{preview.alreadyExist} already exist and will be skipped.</p>
                    )}
                    {!isImport && preview.notFound.length > 0 && (
                        <p className="mb-0 mt-1">{preview.notFound.length} from the file were not found in the system.</p>
                    )}
                </div>

                {preview.errors.length > 0 && (
                    <div className="alert alert-warning">
                        <strong>{preview.errors.length} row(s) had validation errors and will be skipped.</strong>
                        <ul className="mb-0 mt-1">
                            {preview.errors.slice(0, 5).map((err, i) => (
                                <li key={i}>{err.error}</li>
                            ))}
                            {preview.errors.length > 5 && <li>...and {preview.errors.length - 5} more</li>}
                        </ul>
                    </div>
                )}

                {count > 0 && (
                    <div>
                        <button
                            className="btn btn-link p-0 mb-2"
                            onClick={() => this.setState({showDetails: !this.state.showDetails})}
                        >
                            {this.state.showDetails ? 'Hide Details' : 'Show Details'}
                        </button>

                        {this.state.showDetails && (
                            <div className="table-responsive" style={{maxHeight: '300px', overflowY: 'auto'}}>
                                <table className="table table-sm table-striped">
                                    <thead>
                                        <tr>
                                            <th>Name</th>
                                            <th>Address</th>
                                            <th>City</th>
                                            <th>State</th>
                                            <th>Zip</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {items.map((item, i) => (
                                            <tr key={i}>
                                                <td>{item.FacilityName}</td>
                                                <td>{item.FacilityAddress || '-'}</td>
                                                <td>{item.FacilityCity || '-'}</td>
                                                <td>{item.FacilityState || '-'}</td>
                                                <td>{item.FacilityZip || '-'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}

                {count === 0 && (
                    <div className="alert alert-secondary">
                        {isImport
                            ? 'All facilities in the file already exist. Nothing to import.'
                            : 'No matching active facilities found to deactivate.'
                        }
                    </div>
                )}
            </div>
        );
    }

    renderComparingScreen() {
        let {progressProcessed, progressTotal, facilityType} = this.state;
        let percent = progressTotal > 0 ? Math.round((progressProcessed / progressTotal) * 100) : 0;
        let typeLabel = facilityType ? facilityType.toLowerCase() + 's' : 'facilities';

        return (
            <div className="text-center py-4">
                <h5 className="verlag-bold mb-3">Analyzing {typeLabel}...</h5>
                <div className="progress mb-3" style={{height: '25px'}}>
                    <div
                        className="progress-bar progress-bar-striped progress-bar-animated bg-info"
                        role="progressbar"
                        style={{width: `${percent}%`}}
                        aria-valuenow={percent}
                        aria-valuemin={0}
                        aria-valuemax={100}
                    >
                        {percent}%
                    </div>
                </div>
                <p className="text-muted mb-0">
                    {progressTotal > 0
                        ? `${progressProcessed} of ${progressTotal} records compared`
                        : 'Reading file...'
                    }
                </p>
            </div>
        );
    }

    renderProcessingScreen() {
        let {progressProcessed, progressTotal, preview} = this.state;
        let percent = progressTotal > 0 ? Math.round((progressProcessed / progressTotal) * 100) : 0;
        let actionLabel = preview && preview.action === 'ImportNew' ? 'Importing' : 'Deactivating';
        let typeLabel = preview ? preview.facilityType.toLowerCase() + '(s)' : 'facilities';

        return (
            <div className="text-center py-4">
                <h5 className="verlag-bold mb-3">{actionLabel} {typeLabel}...</h5>
                <div className="progress mb-3" style={{height: '25px'}}>
                    <div
                        className="progress-bar progress-bar-striped progress-bar-animated bg-success"
                        role="progressbar"
                        style={{width: `${percent}%`}}
                        aria-valuenow={percent}
                        aria-valuemin={0}
                        aria-valuemax={100}
                    >
                        {percent}%
                    </div>
                </div>
                <p className="text-muted mb-0">
                    {progressProcessed} of {progressTotal} processed
                </p>
            </div>
        );
    }

    render() {
        let {screen, preview} = this.state;
        let isPreview = screen === 'preview';
        let count = isPreview && preview
            ? (preview.action === 'ImportNew' ? preview.toAdd.length : preview.toDeactivate.length)
            : 0;

        return (
            <React.Fragment>
                <Overlay show_loading={this.state.showLoading} zIndex={100003}/>
                <div className="modal fade form_modal" aria-label="Facility Import" id={FacilityImportModal.ID}
                     tabIndex={-1} role="dialog" aria-hidden="true">
                    <div className="modal-dialog modal-lg" role="document">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title verlag-bold">Import Facilities</h5>
                                {screen !== 'processing' && screen !== 'comparing' && (
                                    <button
                                        style={{outline: 'none'}}
                                        type="button"
                                        className="close"
                                        data-dismiss="modal"
                                        aria-label="Close"
                                        onClick={() => this.resetState()}
                                    >
                                        <span aria-hidden="true">&times;</span>
                                    </button>
                                )}
                            </div>
                            <div className="modal-body">
                                {screen === 'upload' && this.renderUploadScreen()}
                                {screen === 'comparing' && this.renderComparingScreen()}
                                {screen === 'preview' && this.renderPreviewScreen()}
                                {screen === 'processing' && this.renderProcessingScreen()}
                            </div>
                            <div className="modal-footer">
                                {screen === 'upload' && (
                                    <button
                                        className="btn immySubmitButtonOutline verlag-bold"
                                        onClick={this.handlePreview}
                                    >
                                        Preview
                                    </button>
                                )}
                                {screen === 'preview' && (
                                    <>
                                        <button
                                            className="btn btn-outline-secondary verlag-bold"
                                            onClick={() => this.resetState()}
                                        >
                                            Back
                                        </button>
                                        {count > 0 && (
                                            <button
                                                className="btn btn-outline-success verlag-bold"
                                                onClick={this.handleConfirm}
                                            >
                                                Confirm {preview.action === 'ImportNew' ? 'Import' : 'Deactivation'}
                                            </button>
                                        )}
                                    </>
                                )}
                                {(screen === 'comparing' || screen === 'processing') && (
                                    <span className="text-muted" style={{fontSize: '0.85em'}}>
                                        Please do not close this window.
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </React.Fragment>
        );
    }
}
